import React from 'react';
import { createPortal } from 'react-dom';
import { TreeGridComponent, ColumnsDirective, ColumnDirective, EditSettingsModel, Inject, Edit, RowDD } from '@syncfusion/ej2-react-treegrid';
import { RowDataBoundEventArgs } from '@syncfusion/ej2-react-grids';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import KnowledgeBookStore from '../../stores/KnowledgeBookStore';
import ViewerStore from '../../stores/ViewerStore';
import ViewerActions from '../../actions/ViewerActions';
import * as Inovx from '@shared';
import FacilityActions from '../../actions/FacilityActions';
import KnowledgeBookActions from '../../actions/KnowledgeBookActions';
import ColorPickerWithNoColorCheckbox from './ColorPickerWithNoColorCheckbox';
import KnowledgeViewForm from './KnowledgeViewForm';
import ContextMenu from '../../viewers/components/ContextMenu';
import ComponentHelpers from '@facility/helpers/ComponentHelpers';

const { useState, useEffect, useRef } = React;

var SystemActions = Inovx.Shared.System.Actions;
interface AssetSetProps {
    treeListRef: React.RefObject<HTMLDivElement>,
    // The panel owns the layout maths and always measures this for us.
    maxHeight: number,
    onDataBound?: () => void,
    searchText?: string,
}
interface IAssetItem {
    ID: string;
    Name: string;
}
interface IAssetSetListItem {
    ID: string;
    Name: string;
    Description?: string;
    Type?: number;
    ItemType: 'root' | 'hierarchy' | 'assetTag' | 'assetSet';
    KnowledgeBookID?: string;
    OntologyID?: string;
    AssetTagIDs?: string[];
    HierarchyItems?: IAssetItem[];
    DisplayProperties?: IDisplayProperties | string;
    Tags?: IViewAnnotationTag[];
    AssetSets?: IKnowledgeViewAssetSet[];
    VersionToken?: string;
    __assetSet?: any;
    __kvEntry?: any
}

interface IAssetSetRootItem extends IAssetSetListItem {
    Items: IAssetSetListItem[];
    isExpanded?: boolean;
    isActive?: boolean;
}

interface ITreeGridRowItem extends IAssetSetListItem {
    parentItem?: IKnowledgeView;
    taskData?: any;
    hasChildRecords?: boolean;
    expanded?: boolean;
    index?: number;
    uniqueID?: string;
    level?: number;
}

interface IOpenMenuState {
    id: string;
    top: number;
    left: number;
}

interface IKnowledgeViewMenuItem {
    id: string;
    label: string;
    iconClass?: string;
    execute: () => void | Promise<void>;
}

const KnowledgeViews = React.forwardRef((props: AssetSetProps, ref) => {
    const [knowledgeBookViews, setKnowledgeBookViews] = useState<IKnowledgeView[]>(
        KnowledgeBookStore.getAllKnowledgeView()
    );

    const [assetSets, setAssetSets] = useState<IAssetSet[]>(
        KnowledgeBookStore.getAllAssetSet()
    );
    const [selectedKnowledgeView, setSelectedKnowledgeView] = useState<IKnowledgeViewDetail>(ViewerStore.getSelectedKnowledgeView());
    const [isKVModalVisible, setIsKVModalVisible] = useState<boolean>(KnowledgeBookStore.getKVModalVisible());
    const treeGridInstance = useRef<TreeGridComponent>(null);
    const toolTipInstance = useRef<TooltipComponent>(null);
    const actionTemplateRendererRef = useRef<(props: any) => React.ReactNode>(() => null);
    const stableActionTemplate = React.useCallback(
        (props: any) => actionTemplateRendererRef.current(props),
        [],
    );

    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const isProgrammaticSelect = useRef(false);
    const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
    const [openMenu, setOpenMenu] = useState<IOpenMenuState | null>(null);
    const is3DViewerVisible: boolean = ViewerStore.is3DViewerVisible();
    const [visibleRowsHeight, setVisibleRowsHeight] = useState(0);
    const [isRowDragging, setIsRowDragging] = useState(false);

    React.useEffect(() => {
        if (!isRowDragging) return;
        const finishDrag = () => setIsRowDragging(false);
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') finishDrag();
        };
        document.addEventListener('mouseup', finishDrag);
        document.addEventListener('touchend', finishDrag);
        document.addEventListener('touchcancel', finishDrag);
        document.addEventListener('dragend', finishDrag);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('blur', finishDrag);
        return () => {
            document.removeEventListener('mouseup', finishDrag);
            document.removeEventListener('touchend', finishDrag);
            document.removeEventListener('touchcancel', finishDrag);
            document.removeEventListener('dragend', finishDrag);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('blur', finishDrag);
        };
    }, [isRowDragging]);
    const visibleRowsFrameRef = useRef<number | null>(null);
    const mountedRef = React.useRef(false);

    const selectedKnowledgeBook = KnowledgeBookStore.getSelectedKnowledgeBook();

    const canModifyKnowledgeView = ComponentHelpers.hasModifyKnowledgeBookPermission(selectedKnowledgeBook);
    const canManageKnowledgeView = ComponentHelpers.hasManageKnowledgeBookPermission(selectedKnowledgeBook);

    /* ---------------- STORE LISTENERS ---------------- */
    const onChange = React.useCallback(() => {
        if (!mountedRef.current) return;

        setIsKVModalVisible(KnowledgeBookStore.getKVModalVisible());
        setAssetSets(KnowledgeBookStore.getAllAssetSet());
        setKnowledgeBookViews(KnowledgeBookStore.getAllKnowledgeView());
        setSelectedKnowledgeView(ViewerStore.getSelectedKnowledgeView())
    }, []);
    useEffect(() => {
        mountedRef.current = true;

        ViewerStore.addChangeListener(onChange);
        KnowledgeBookStore.addChangeListener(onChange);

        return () => {
            mountedRef.current = false;
            if (visibleRowsFrameRef.current != null) {
                cancelAnimationFrame(visibleRowsFrameRef.current);
                visibleRowsFrameRef.current = null;
            }
            ViewerStore.removeChangeListener(onChange);
            KnowledgeBookStore.removeChangeListener(onChange);
        };
    }, [onChange]);

    useEffect(() => {
        setOpenMenu(null);
    }, [selectedKnowledgeView?.ID, knowledgeBookViews.length, isKVModalVisible, props.searchText]);

    /* ---------------- TREE DATA (STABLE) ---------------- */

    const buildAssetSetChildrenForKV = (kv: IKnowledgeViewDetail, all: IAssetSet[]) => {
        const kvAssetSets: IKnowledgeViewAssetSet[] = kv?.AssetSets || [];
        const map = new Map<string, IKnowledgeViewAssetSet>();
        (kvAssetSets ?? []).forEach(a => map.set(a.ID, a));

        const checked: IAssetSetListItem[] = [];
        const uncheckedAll: IAssetSetListItem[] = [];
        const uncheckedNormal: IAssetSetListItem[] = [];

        for (const a of all) {
            let entry: IKnowledgeViewAssetSet | null = null;
            const tagId = a.ID;
            if (map.has(tagId)) {
                entry = map.get(tagId);
            }

            const node: IAssetSetListItem = {
                ID: a.ID,
                Name: a.Type === AssetSetType.All3DAssets
                    ? InovxLoader.t('facility:assetSetVisualization.all3DAssets')
                    : a.Type === AssetSetType.AllAssetTags
                        ? InovxLoader.t('facility:assetSetVisualization.allAssetTags')
                        : a.Name,
                Type: a.Type,
                ItemType: "assetSet",
                AssetTagIDs: a.AssetTagIDs,
                __assetSet: a,
                __kvEntry: entry
            };

            if (entry) {
                checked.push(node);
            } else if (a.Type === AssetSetType.All3DAssets || a.Type === AssetSetType.AllAssetTags) {
                uncheckedAll.push(node);
            } else {
                uncheckedNormal.push(node);
            }
        }

        checked.sort((a, b) => (a.__kvEntry?.Ord ?? 0) - (b.__kvEntry?.Ord ?? 0));
        uncheckedAll.sort((a, b) => a.Name.localeCompare(b.Name));
        uncheckedNormal.sort((a, b) => a.Name.localeCompare(b.Name));

        return [...checked, ...uncheckedAll, ...uncheckedNormal];
    };


    /* ---------------- EXPAND / COLLAPSE ---------------- */

    const updateExpandedStatus = (args: any) => {
        const id = args?.data?.ID;
        if (!id) return;

        setExpandedRows(prev => {
            if (args.name === 'expanded') {
                return prev.includes(id) ? prev : [...prev, id];
            }
            if (args.name === 'collapsed') {
                return prev.filter(x => x !== id);
            }
            return prev;
        });
        updateVisibleRows();
    };

    /* ---------------- ROW SELECT ---------------- */

    const handleRowSelecting = async (args: any) => {
        
        if (isProgrammaticSelect.current) {
            args.cancel = true;
            isProgrammaticSelect.current = false;
            return;
        }

        const item = args.data;

        // Disallow selection of child rows
        if (item?.ItemType !== "root") {
            args.cancel = true;
            return;
        }

        const target = args?.target;

        // Do not select KV if clicked inside action icons
        if (target?.closest(".tree-actions")) {
            args.cancel = true;
            return;
        }

        // Only select KV when root row is clicked normally
        if (item.ItemType === "root") {
            // If clicking on already selected KV → do nothing
            if (item.ID === selectedKnowledgeView?.ID) {
                //args.cancel = true;
                return;
            }

            await FacilityActions.openKnowledgeViewInViewer(item);
        }

    };

    /* ---------------- TEMPLATES ---------------- */

    const applyAssetSetColor = (item: any, color: number | null) => {
        const kv = ViewerStore.getSelectedKnowledgeView(); // Get fresh copy (callback may be stale)
        if (!kv) return;

        const idx = kv.AssetSets.findIndex(x => x.ID === item.ID);
        if (idx === -1) return;

        const updatedAssetSets = kv.AssetSets.map((entry, entryIdx) =>
            entryIdx === idx ? { ...entry, Color: color } : entry
        );

        const updatedKv: IKnowledgeViewDetail = { ...kv, AssetSets: updatedAssetSets };
        ViewerActions.setSelectedKnowledgeViewData(updatedKv);
    };

    const applyAssetSetHidden = (item: any, hidden: boolean) => {
        const kv = ViewerStore.getSelectedKnowledgeView();
        if (!kv) return;

        const updatedAssetSets = (kv.AssetSets ?? []).map(entry =>
            entry.ID === item.ID ? { ...entry, Hidden: hidden } : entry
        );
        ViewerActions.setSelectedKnowledgeViewData({ ...kv, AssetSets: updatedAssetSets });
    };

    const isSelectedKnowledgeView = (knowledgeViewId: string) => {
        const kv = ViewerStore.getSelectedKnowledgeView();
        return kv?.ID === knowledgeViewId;
    };

    const getSourceKnowledgeViewDetail = async (knowledgeView: IKnowledgeView) => {
        if (isSelectedKnowledgeView(knowledgeView.ID)) {
            return structuredClone(ViewerStore.getSelectedKnowledgeView());
        }

        return await KnowledgeBookActions.getKnowledgeViewDetail(knowledgeView.ID);
    };

    const cloneKnowledgeView = async (knowledgeView: IKnowledgeView) => {
        const sourceKnowledgeView = await getSourceKnowledgeViewDetail(knowledgeView);
        if (!sourceKnowledgeView) {
            return;
        }

        const clonedDisplayProperties = structuredClone(sourceKnowledgeView.DisplayProperties);
        const clonedAssetSets = (sourceKnowledgeView.AssetSets ?? []).map(assetSet => ({
            ID: assetSet.ID,
            Ord: assetSet.Ord,
            Color: assetSet.Color ?? null,
            Hidden: !!assetSet.Hidden,
        }));

        const cloneNamePrefix = InovxLoader.t('facility:knowledgeBook.copyOfPrefix');
        const createdKnowledgeView = await KnowledgeBookActions.createKnowledgeView({
            ID: null,
            KVImage: sourceKnowledgeView.KVImage || '',
            VersionToken: null,
            Name: `${cloneNamePrefix}${sourceKnowledgeView.Name}`,
            Description: sourceKnowledgeView.Description,
            KnowledgeBookID: sourceKnowledgeView.KnowledgeBookID,
            ViewID: sourceKnowledgeView.ViewID,
            Tags: sourceKnowledgeView.Tags ?? [],
            DisplayProperties: clonedDisplayProperties,
            View: sourceKnowledgeView.View ?? null,
            Type: sourceKnowledgeView.Type,
            OntologyID: sourceKnowledgeView.OntologyID,
            AssetSets: clonedAssetSets,
        } as IKnowledgeViewDetail);

        const createdKnowledgeViewDetail = await KnowledgeBookActions.getKnowledgeViewDetail(createdKnowledgeView.ID);
        await KnowledgeBookActions.updateKnowledgeView({
            ...createdKnowledgeViewDetail,
            Name: `${cloneNamePrefix}${sourceKnowledgeView.Name}`,
            Description: sourceKnowledgeView.Description,
            DisplayProperties: clonedDisplayProperties,
            AssetSets: clonedAssetSets,
        });
    };

    const handleActionMouseDown = (
        event: React.MouseEvent<HTMLElement>,
        action: () => void | Promise<void>,
    ) => {
        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent?.stopImmediatePropagation();
        void action();
    };

    const buildKnowledgeViewMenuItems = (knowledgeView: IKnowledgeView): IKnowledgeViewMenuItem[] => {
        const menuItems: IKnowledgeViewMenuItem[] = [];

        if (canModifyKnowledgeView) {
            menuItems.push({
                id: 'clone',
                label: InovxLoader.t('facility:action.cloneView'),
                iconClass: 'clone-icon',
                execute: async () => {
                    setOpenMenu(null);
                    await cloneKnowledgeView(knowledgeView);
                },
            });
        }

        if (canManageKnowledgeView) {
            menuItems.push({
                id: 'delete',
                label: InovxLoader.t('facility:action.deleteView'),
                iconClass: 'delete-icon',
                execute: () => {
                    setOpenMenu(null);
                    deleteKnowledgeView(knowledgeView);
                },
            });
        }

        return menuItems;
    };

    const toggleMenu = (event: React.MouseEvent<HTMLElement>, knowledgeViewId: string) => {
        const triggerRect = event.currentTarget.getBoundingClientRect();
        setOpenMenu(currentMenu => {
            if (currentMenu?.id === knowledgeViewId) {
                return null;
            }

            return {
                id: knowledgeViewId,
                top: triggerRect.top - 2,
                left: triggerRect.right + 4,
            };
        });
    };

    const actionTemplate = (props: any) => {
        const isParent = props.ItemType === 'root';
        const canShowMenu = isParent && (canModifyKnowledgeView || canManageKnowledgeView);
        const menuItems = isParent ? buildKnowledgeViewMenuItems(props) : [];

        return (
            <div className="tree-actions">
                {isParent && (
                    <>
                        {canShowMenu && menuItems.length > 0 && (
                            <div className="kv-menu-wrapper">
                                <button
                                    type="button"
                                    className="kv-menu-trigger"
                                    title={InovxLoader.t('facility:action.moreActions')}
                                    onClick={event => {
                                        handleActionMouseDown(event, () => {
                                            toggleMenu(event, props.ID);
                                        });
                                    }}
                                ><span className="kv-list-overflow-icon">&#8942;</span></button>
                            </div>
                        )}
                    </>
                )}

                {!isParent && (
                    <>
                        <button
                            type="button"
                            className={`kv-hidden-toggle${props.__kvEntry?.Hidden ? ' is-hidden' : ''}`}
                            disabled={(!props.__kvEntry) || !canModifyKnowledgeView}
                            aria-pressed={!!props.__kvEntry?.Hidden}
                            title={InovxLoader.t(props.__kvEntry?.Hidden
                                ? 'facility:assetSetVisualization.show'
                                : 'facility:assetSetVisualization.hide')}
                            onClick={(event) => {
                                event.stopPropagation();
                                applyAssetSetHidden(props, !props.__kvEntry?.Hidden);
                            }}
                        >
                            <i
                                className={`fa ${props.__kvEntry?.Hidden ? 'fa-eye' : 'fa-eye-slash'}`}
                                aria-hidden="true"
                            />
                        </button>
                        <ColorPickerWithNoColorCheckbox
                            id={`kv-color-${props.ID}`}
                            value={props.__kvEntry?.Color}
                            disabled={(!props.__kvEntry) || !!props.__kvEntry?.Hidden || !canModifyKnowledgeView}
                            onChange={(value) => applyAssetSetColor(props, value)}
                        />
                    </>
                )}
            </div>
        );
    };
    actionTemplateRendererRef.current = actionTemplate;

    const assetSetHasTags = (item: ITreeGridRowItem | IAssetSetListItem) => {
        const assetSet = item?.__assetSet;

        return Array.isArray(assetSet?.AssetTagIDs) && assetSet.AssetTagIDs.length > 0;
    };

    const handleRowDataBound = (args: RowDataBoundEventArgs) => {
        const item = args.data as ITreeGridRowItem;
        args.row.classList.add('cursor-pointer');
        
        if (item.ItemType === 'root') {
            args.row.classList.add('kv-item');
            return;
        }

        if (item.ItemType !== 'assetSet') return;

        const hasTags = assetSetHasTags(item);

        if (!is3DViewerVisible && !hasTags) {
            args.row.classList.add('asset-set-no-tags-non3d');
        }

        if (!item.__kvEntry) {
            args.row.classList.add('no-drag');
            args.row.setAttribute('aria-grabbed', 'false');
        } else {
            args.row.classList.add('asset-sets');
        }

    };

    const handleQueryCellInfo = (args: any) => {

        if (args.column.field !== 'Name') return;

        const item = args.data as ITreeGridRowItem;

        const cell = args.cell as HTMLElement;
        const treeCell = cell.querySelector('.e-treecell') as HTMLElement;
        if (!treeCell) return;

        if (treeCell.querySelector('.tree-custom-wrapper')) return;

        const text = treeCell.textContent?.trim() ?? '';
        const hasTags = assetSetHasTags(item);
        const textStyle = (!is3DViewerVisible && !hasTags)
            ? 'font-style: italic; opacity: 0.5;'
            : '';

        if (item.ItemType === 'root') {
            if (treeCell.querySelector('.parent-tree-name-text')) return;

            treeCell.innerHTML = `
            <span class="parent-tree-name-text display-name-text"
                  title="${text}">
                ${text}
            </span>
        `;
            return;
        }
        let isDisabled = !(canModifyKnowledgeView || canManageKnowledgeView);
        treeCell.innerHTML = `
        <div class="tree-custom-wrapper" style="display:flex;align-items:center;gap:8px;">
            <input type="checkbox" class="row-checkbox"
                ${item.__kvEntry ? 'checked' : ''} ${isDisabled ? 'disabled' : ''}/>
            <span class="tree-name-text display-name-text" title="${text}" style="${textStyle}">${text}</span>
        </div>
    `;

        const checkbox = treeCell.querySelector('.row-checkbox') as HTMLInputElement;

        checkbox?.addEventListener('click', (e) => {
            e.stopPropagation();

            const selKV = ViewerStore.getSelectedKnowledgeView(); // Get fresh copy (callback may be stale)

            if (selKV?.ID !== item.parentItem?.ID) {
                console.warn("Mismatched Knowledge View IDs in checkbox click");
                return;
            }

            const kv = selKV;
            const current = kv.AssetSets ?? [];
            
            let updated = [...current];
            updated.sort((a, b) => a.Ord - b.Ord);

            const idx = updated.findIndex(x => x.ID === item.ID);

            if (checkbox.checked && idx === -1) {
                updated.push({
                    ID: item.ID,
                    Ord: updated.length + 1,
                    Color: null,
                    Hidden: false
                });
            }

            if (!checkbox.checked && idx !== -1) {
                updated[idx].Ord = -1; // Mark for removal
            }

            let newOrd = 1;
            updated = updated.map((x) => {
                return { ...x, Ord: (x.Ord >= 0) ? newOrd++ : x.Ord };
            });

            kv.AssetSets = updated;
            ViewerActions.setSelectedKnowledgeViewData(kv);
        });
    };

    /* ---------------- ACTIONS ---------------- */

    const deleteKnowledgeView = (args: IKnowledgeView) => {
        const remainingKV = KnowledgeBookStore.getAllKnowledgeView();
        if (remainingKV.length > 1) {
            if (ViewerStore.getSelectedKnowledgeView()?.ID === args.ID && ViewerActions.checkForKnowledgeViewChanges(() => deleteKnowledgeView(args))) return;
            SystemActions.displayConfirmationMessage({
                message: InovxLoader.t('facility:message.confirmDeleteKV'),
                confirmCallback: async () => {
                    const selectedKV = ViewerStore.getSelectedKnowledgeView();
                    const isDeletingSelectedKnowledgeView = selectedKV?.ID === args.ID;
                    if (isDeletingSelectedKnowledgeView) {
                        ViewerActions.closeKnowledgeView();
                    }

                    await KnowledgeBookActions.deleteKnowledgeView(args);

                    if (isDeletingSelectedKnowledgeView) {
                        const firstRemainingKnowledgeView = KnowledgeBookStore.getAllKnowledgeView()[0];
                        if (firstRemainingKnowledgeView) {
                            await FacilityActions.openKnowledgeViewInViewer(firstRemainingKnowledgeView);
                        }
                    }
                }
            });
        } else {
            SystemActions.displayWarningMessage({ message: InovxLoader.t('facility:message.deleteKVWarning') })
        }
    };


    /* ---------------- RENDER ---------------- */

    const gridHeight = Math.max(0, props.maxHeight);
    const normalizedSearchText = props.searchText?.trim().toLocaleLowerCase() ?? '';
    
    const treeDataSource = React.useMemo<IAssetSetRootItem[]>(() => {
        const kvs = normalizedSearchText
            ? knowledgeBookViews.filter(kv => kv.Name?.toLocaleLowerCase().includes(normalizedSearchText))
            : knowledgeBookViews;
        return kvs.map(kv => {
            const isActiveKv = kv.ID === selectedKnowledgeView?.ID;

            return {
                ID: kv.ID,
                Name: kv.Name,
                Description: kv.Description,
                ItemType: 'root',
                Type: kv.Type,
                Items: [],
                hasChildRecords: false,
                isExpanded: false,
                KnowledgeBookID: kv.KnowledgeBookID,
                OntologyID: kv.OntologyID,
                VersionToken: kv.VersionToken,
                isActive: isActiveKv
            };
        });
    }, [knowledgeBookViews, assetSets, selectedKnowledgeView, normalizedSearchText]);

    const handleDataBound = () => {
        const grid = treeGridInstance.current;
        if (!grid) return;
        updateVisibleRows();
        props.onDataBound?.();
        const content = grid.getContent() as HTMLElement | null;
        if (content) {
            content.scrollLeft = 0;
        }
        grid.clearSelection();
        const kvId = selectedKnowledgeView?.ID;
        //if (!kvId) return;

        if (!kvId) {
            const activeCells = grid.element.querySelectorAll('td.e-active');
            activeCells.forEach((cell) => {
                cell.classList.remove('e-active');
            });

            setSelectedRowIndex(null);
            return;
        }

        const records = grid.getCurrentViewRecords() as any[];

        const index = records.findIndex(
            r => r.ItemType === 'root' && r.ID === kvId
        );

        if (index !== -1) {
            setSelectedRowIndex(index !== -1 ? index : null);
            
            grid.selectRow(index);
        }
    };

    useEffect(() => {
        if (selectedRowIndex != null) {
            scrollToRow(selectedRowIndex);
        }
    }, [selectedRowIndex]);

    const scrollToRow = (rowIndex: number) => {
        if (treeGridInstance.current) {
            const rowElement = treeGridInstance.current.getRowByIndex(rowIndex) as HTMLTableRowElement; // Get the row element
            if (rowElement) {
                const contentElement = treeGridInstance.current.getContent().firstChild as Element;
                const rowTop = rowElement.offsetTop; // Row's top position relative to the container
                const rowBottom = rowTop + rowElement.offsetHeight; // Row's bottom position
                const contentScrollTop = contentElement.scrollTop; // Current scroll position
                const contentBottom = contentScrollTop + contentElement.clientHeight; // Visible bottom position

                // Scroll to the row
                if (!(rowTop >= contentScrollTop && rowBottom <= contentBottom)) {
                    contentElement.scrollTop = rowTop;
                }
            }
        }
    };

    const getRootRecords = () => {
        const grid = treeGridInstance.current;
        if (!grid) return [];

        const records = grid.getCurrentViewRecords() as ITreeGridRowItem[];

        return records.filter(r => r.ItemType === 'root');
    };

    const getRootIndexFromDropIndex = (dropIndex: number) => {
        const grid = treeGridInstance.current;
        if (!grid) return -1;

        const records = grid.getCurrentViewRecords() as ITreeGridRowItem[];

        const target = records[dropIndex];
        if (!target) return -1;

        // If drop happens on a child → find its parent KV
        if (target.ItemType !== 'root') {
            return records
                .filter(r => r.ItemType === 'root')
                .findIndex(r => r.ID === target.parentItem?.ID);
        }

        // Drop directly on a root
        return records
            .filter(r => r.ItemType === 'root')
            .findIndex(r => r.ID === target.ID);
    };


    const reorderKnowledgeViews = (fromIndex: number, dropIndex: number) => {
        // Clone KV list
        const kvList = structuredClone(knowledgeBookViews);

        const [moved] = kvList.splice(fromIndex, 1);
        kvList.splice(dropIndex, 0, moved);

        const ids = kvList.map(kv => kv.ID);
        const selectedKnowledgeBook = KnowledgeBookStore.getSelectedKnowledgeBook();

        KnowledgeBookActions.setKnowledgeViewOrder(selectedKnowledgeBook, ids);
    }

    const handleRowDragStartHelper = (args: any) => {
        const dragged = args.data?.[0];
        if (!dragged) return;

        // Only allow root OR asset rows
        if (dragged.ItemType !== 'root' && dragged.ItemType !== 'assetSet') {
            args.cancel = true;
            return;
        }
        if (dragged.ItemType === 'assetSet' && !dragged.__kvEntry) {
            args.cancel = true;
            return;
        }
    };

    const getParentIndex = (fromIndex: number, dropIndex: number) => {
        const roots = getRootRecords();
        if (!roots.length) return;

        const fromRootIndex = roots.findIndex(
            r => r.index === fromIndex || r.uniqueID === roots[fromIndex]?.uniqueID
        );

        const toRootIndex = getRootIndexFromDropIndex(dropIndex);
        return { fromRootIndex, toRootIndex };
    } 

    const handleRowDrop = (args: any) => {
        setIsRowDragging(false);
        const grid = treeGridInstance.current;
        if (!grid) return;

        const draggedView = args.data?.[0] as ITreeGridRowItem | undefined;
        const dropElement = args.target instanceof Element ? args.target : null;
        const content = dropElement?.closest('.e-content');
        // The grid reports blank space below its final row as an invalid drop.
        // Only accept that space inside this grid, with the end of the list visible.
        if (draggedView?.ItemType === 'root' && content && grid.element.contains(content)
            && !dropElement?.closest('tr')
            && content.scrollTop + content.clientHeight >= content.scrollHeight - 1) {
            args.cancel = true;
            const fromIndex = knowledgeBookViews.findIndex(view => view.ID === draggedView.ID);
            if (fromIndex >= 0 && fromIndex < knowledgeBookViews.length - 1) {
                reorderKnowledgeViews(fromIndex, knowledgeBookViews.length - 1);
            }
            return;
        }

        if (args?.dropPosition === "middleSegment" || args?.dropPosition === "Invalid") {
            args.cancel = true;
            return;
        }
        const records = grid.getCurrentViewRecords() as any[];

        const draggedItem = args.data?.[0] as ITreeGridRowItem;
        const targetItem = records[args.dropIndex];

        if (!draggedItem || !targetItem) {
            args.cancel = true;
            return;
        }

        if (!targetItem) {
            args.cancel = true;
            return;
        }
        /* ------------------------------------
           RULE 1: Root rows (KV)
           ------------------------------------ */
        if (draggedItem.ItemType === 'root') {
            // Root can only drop on another root
            if (targetItem.ItemType !== 'root') {
                args.cancel = true;
                return;
            }
            let fromIndex = args.fromIndex;
            let dropIndex = args.dropIndex;
            let { fromRootIndex, toRootIndex } = getParentIndex(fromIndex, dropIndex)
            if (fromRootIndex > toRootIndex) {
                if (args?.dropPosition === "bottomSegment" && toRootIndex > -1) {
                    toRootIndex = toRootIndex + 1;
                }
            } else {
                if (args?.dropPosition === "topSegment" && toRootIndex > 0) {
                    toRootIndex = toRootIndex - 1;
                }
            }
            if (fromRootIndex === toRootIndex) {
                args.cancel = true;
                return;
            }
            reorderKnowledgeViews(fromRootIndex, toRootIndex);
        }else if (draggedItem.ItemType === 'assetSet') {
            if (!draggedItem.__kvEntry) {
                args.cancel = true;
                return;
            }
            const draggedParentId = draggedItem?.parentItem?.ID;
            const targetParentId =
                targetItem.ItemType === 'assetSet'
                    ? targetItem.parentItem?.ID
                    : targetItem.ID;

            // Block moving child across parents
            if (draggedParentId !== targetParentId) {
                args.cancel = true;
                return;
            }
            if (targetItem.ItemType !== 'assetSet' || !targetItem.__kvEntry) {
                args.cancel = true;
                return;
             }
            // Reorder asset sets inside the same KV
            //reorderAssetSets(draggedItem, targetItem);
            const kv = ViewerStore.getSelectedKnowledgeView();
            if (!kv?.AssetSets) {
                args.cancel = true;
                return;
            }

            // Sort asset sets by Ord
            const ordered = [...kv.AssetSets].sort(
                (a, b) => (a.Ord ?? 0) - (b.Ord ?? 0)
            );

            let fromIndex = ordered.findIndex(x => x.ID === draggedItem.ID);
            let toIndex = ordered.findIndex(x => x.ID === targetItem.ID);

            if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
                args.cancel = true;
                return;
            }

            if (fromIndex > toIndex) {
                // dragging bottom → top
                if (args.dropPosition === 'bottomSegment') {
                    toIndex++;
                }
            } else {
                // dragging top → bottom
                if (args.dropPosition === 'topSegment') {
                    toIndex--;
                }
            }

            // Clamp index
            toIndex = Math.max(0, Math.min(toIndex, ordered.length - 1));

            if (fromIndex === toIndex) {
                args.cancel = true;
                return;
            }

            // Reorder
            const [moved] = ordered.splice(fromIndex, 1);
            ordered.splice(toIndex, 0, moved);

            // Reassign Ord
            kv.AssetSets = ordered.map((x, i) => ({
                ...x,
                Ord: i + 1
            }));

            setTimeout(() => {
                ViewerActions.setSelectedKnowledgeViewData(kv);
            }, 10);
        }
    };


    const ROW_HEIGHT = 36;
    const MAX_HEIGHT = gridHeight;

    const updateVisibleRows = () => {
        if (visibleRowsFrameRef.current != null) {
            cancelAnimationFrame(visibleRowsFrameRef.current);
        }

        visibleRowsFrameRef.current = requestAnimationFrame(() => {
            visibleRowsFrameRef.current = null;
            const grid = treeGridInstance.current;
            if (!grid || !mountedRef.current) return;

            const nextHeight = Math.ceil(grid.getRows().reduce((height, row) => {
                const rowHeight = row.getBoundingClientRect().height;
                return rowHeight > 0 ? height + rowHeight : height;
            }, 0));

            setVisibleRowsHeight(currentHeight => currentHeight === nextHeight ? currentHeight : nextHeight);
        });
    };

    const calculatedHeight =
        // Include the end-drop space and the grid's top/bottom borders.
        Math.max(ROW_HEIGHT, visibleRowsHeight) + (isRowDragging ? 16 : 0) + 2;

    const safeMaxHeight = Number.isFinite(MAX_HEIGHT) ? MAX_HEIGHT : 180;
    const finalHeight = Math.min(safeMaxHeight, calculatedHeight);

    useEffect(() => {
        const treeGrid = treeGridInstance.current;
        const container = treeGrid?.element.parentElement;
        if (!treeGrid || !container) return;

        // The retained grid can bind while its panel is hidden. Reapply its
        // scroll height when the container is laid out again, even when the
        // React height prop has not changed.
        const observer = new ResizeObserver(() => {
            if (treeGrid.isDestroyed || container.clientHeight === 0) return;
            const grid = treeGrid.grid;
            grid.height = Math.max(0, container.clientHeight - 2);
            grid.dataBind();
            grid.scrollModule.setHeight();
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    const openMenuKnowledgeView = openMenu
        ? knowledgeBookViews.find(knowledgeView => knowledgeView.ID === openMenu.id)
        : null;
    const openMenuItems = openMenuKnowledgeView ? buildKnowledgeViewMenuItems(openMenuKnowledgeView) : [];

    return (
        <div className="tree-list dragable-tbl tree-list-no-header" ref={props.treeListRef}>
            {isKVModalVisible && <KnowledgeViewForm />}
            <TooltipComponent
                ref={toolTipInstance}
                target=".display-name-text"
                beforeRender={args => {
                    const el = args.target as HTMLElement;
                    if (!el) {
                        args.cancel = true;
                        return;
                    }

                    // Show tooltip only if text is truncated
                    if (el.scrollWidth <= el.clientWidth) {
                        args.cancel = true;
                    }
                }}
                showTipPointer={false}
                cssClass="knowledge-view-name-tooltip"
            >
                <div
                    className={`knowledge-views-grid-container${treeDataSource.length === 0 ? ' is-empty' : ''}${isRowDragging ? ' is-row-dragging' : ''}`}
                    style={{
                        height: finalHeight,
                        maxHeight: MAX_HEIGHT,
                        minHeight: 0,
                        overflow: 'hidden'
                    }}
                >
                    <TreeGridComponent
                        ref={treeGridInstance}
                        height={Math.max(0, finalHeight - 2)}
                        dataSource={treeDataSource}
                        dataBound={handleDataBound}
                        childMapping="Items"
                        expandStateMapping="isExpanded"
                        expanded={updateExpandedStatus}
                        collapsed={updateExpandedStatus}
                        treeColumnIndex={1}
                        selectedRowIndex={selectedRowIndex ?? undefined}
                        rowHeight={ROW_HEIGHT}
                        allowSelection
                        rowSelecting={handleRowSelecting}
                        allowRowDragAndDrop={!normalizedSearchText && (canModifyKnowledgeView || canManageKnowledgeView)}
                        rowDragStartHelper={handleRowDragStartHelper}
                        rowDragStart={() => setIsRowDragging(true)}
                        rowDrop={handleRowDrop}
                        selectionSettings={{
                            mode: 'Row',
                            type: 'Single',
                            enableToggle: false,
                            persistSelection: false
                        }}
                        rowDataBound={(e) => { handleRowDataBound(e) }}
                        queryCellInfo={handleQueryCellInfo}
                        allowKeyboard={false}
                >
                    <ColumnsDirective>
                        <ColumnDirective field="ID" isPrimaryKey={true} visible={false} />
                        <ColumnDirective field="Name" headerText="" allowEditing={false} customAttributes={{ class: 'parent-tree-name-text' }} />
                        {(canModifyKnowledgeView || canManageKnowledgeView) && <ColumnDirective width="15" allowEditing={false} template={stableActionTemplate} />}
                    </ColumnsDirective>
                    <Inject services={[Edit, RowDD]} />
                    </TreeGridComponent>
                {treeDataSource.length === 0 && (
                    <div className="knowledge-views-empty-message">
                        {InovxLoader.t(normalizedSearchText
                            ? 'facility:knowledgeBook.knowledgeViewsNoSearchResults'
                            : 'facility:message.noKnowledgeViewAdded')}
                    </div>
                )}
                </div>
            </TooltipComponent>
            {openMenu && openMenuKnowledgeView && openMenuItems.length > 0 && createPortal(
                <ContextMenu
                    left={openMenu.left}
                    top={openMenu.top}
                    items={openMenuItems.map(menuItem => ({
                        id: menuItem.id,
                        label: menuItem.label,
                        iconClass: menuItem.iconClass,
                    }))}
                    onItemClick={(itemId) => {
                        const selectedMenuItem = openMenuItems.find(menuItem => menuItem.id === itemId);
                        if (selectedMenuItem) {
                            void selectedMenuItem.execute();
                        }
                    }}
                    onRequestClose={() => setOpenMenu(null)}
                    containerId="knowledge-view-context-menu"
                    zIndex={2000}
                />,
                document.body,
            )}
        </div>
    );
});


export default KnowledgeViews;
